import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  MenuSharp,
} from "@mui/icons-material";
import { NavLink, useLocation } from "react-router-dom";
import sidebar  from "./layoutData";

// Custom Tooltip (unchanged)
const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  "& .MuiTooltip-tooltip": {
    backgroundColor: "#de6b14",
    color: "#fff",
    fontSize: "12px",
    padding: "6px 12px",
    borderRadius: "4px",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
    fontWeight: 400,
  },
  "& .MuiTooltip-arrow": {
    color: "#de6b14",
  },
}));

const Sidebar = ({ userEmail = "user@example.com" }) => {
  const [open, setOpen] = useState(true);
  const [submenuOpen, setSubmenuOpen] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoverPositions, setHoverPositions] = useState({});
  const [hoverPath, setHoverPath] = useState([]);
  const [activeSubmenu, setActiveSubmenu] = useState("");
  const hoverTimeout = useRef(null);
  const location = useLocation();

  const toggleDrawer = () => setOpen((prev) => !prev);

const toggleSubmenu = (name) => {
  setSubmenuOpen((prev) => ({ ...prev, [name]: !prev[name] }));
};

  const isItemActive = (item) =>
    location.pathname === item.link || location.pathname === item.path;

  const hasActiveChild = (item) =>
    item.submenu?.some(
      (sub) => isItemActive(sub) || (sub.submenu && hasActiveChild(sub))
    );

  // Update activeSubmenu for collapsed mode
  useEffect(() => {
    if (!open) {
      const findByPath = (items, path) => {
        for (const it of items) {
          const p = it.link || it.path;
          if (p === path) return it.name;
          if (it.submenu) {
            const found = findByPath(it.submenu, path);
            if (found) return found;
          }
        }
        return null;
      };
      const matched = findByPath(sidebar.buyer, location.pathname);
      setActiveSubmenu(matched || "");
    } else {
      setActiveSubmenu("");
    }
  }, [location.pathname, open]);

  const handleMouseEnter = (event, item, level = 0) => {
    if (!open && item.submenu) {
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
      const rect = event.currentTarget.getBoundingClientRect();
      const top = Math.min(rect.top, window.innerHeight - 300);
      const left = rect.right + 8 + level * 220;
      setHoverPositions((prev) => ({
        ...prev,
        [level]: { top, left },
      }));
      setHoveredItem(item);
      setHoverPath((prev) => [...prev.slice(0, level), item.name]);
    }
  };

  const handleMouseLeave = (item, level = 0) => {
    if (!open) {
      hoverTimeout.current = setTimeout(() => {
        setHoverPath((prev) => prev.slice(0, level));
        if (hoverPath.length <= level) {
          setHoveredItem(null);
          setHoverPositions((prev) => {
            const newPositions = { ...prev };
            delete newPositions[level];
            return newPositions;
          });
        }
      }, 250);
    }
  };

  // Recursive render
  const renderItem = (item, level = 0) => {
  const isActive = isItemActive(item);
  const isChildActive = hasActiveChild(item);
  const isExpanded = submenuOpen[item.name];
  const isHovered =
    hoveredItem?.name === item.name || hoverPath.includes(item.name);

  const expandedActive = open
    ? isActive || isChildActive
    : isActive || isChildActive || activeSubmenu === item.name;

  // Conditionally wrap ListItem with Tooltip when sidebar is collapsed and item has no submenu
  const listItem = (
    <ListItem
      button
      component={!item.submenu ? NavLink : "div"}
      to={item.link || item.path}
      onClick={() => {
        if (!item.submenu) {
          setActiveSubmenu(item.name);
        } else {
          toggleSubmenu(item.name);
        }
      }}
      onMouseEnter={(e) => handleMouseEnter(e, item, level)}
      onMouseLeave={() => handleMouseLeave(item, level)}
      sx={{
        pl: 2 + level * 2,
        color: expandedActive ? "#de6b14" : "#fff",
        backgroundColor: expandedActive ? "#fff" : "#de6b14",
        ...(isChildActive && !isActive && !expandedActive
          ? { backgroundColor: "#e07b30", color: "#fff" }
          : {}),
        "& .MuiListItemIcon-root": {
          color: expandedActive ? "#de6b14" : "#fff",
          minWidth: "25px",
        },
        "&:hover": {
          backgroundColor: "#fff",
          color: "#de6b14",
          "& .MuiListItemIcon-root": {
            color: "#de6b14",
          },
        },
      }}
    >
      {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
      {open && <ListItemText primary={item.name} />}
      {item.submenu && open && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
    </ListItem>
  );

  return (
    <Box key={item.name}>
      {/* Add tooltip for items without submenu when sidebar is collapsed */}
      {!open && !item.submenu ? (
        <CustomTooltip title={item.name} placement="right" arrow>
          {listItem}
        </CustomTooltip>
      ) : (
        listItem
      )}

      {/* Floating submenu for collapsed mode */}
      {!open && item.submenu && isHovered && hoverPositions[level] && (
        <Paper
          onMouseEnter={() => {
            if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
            setHoverPath((prev) => [...prev.slice(0, level + 1), item.name]);
          }}
          onMouseLeave={() => handleMouseLeave(item, level)}
          sx={{
            position: "fixed",
            top: hoverPositions[level]?.top || 0,
            left: hoverPositions[level]?.left || 0,
            backgroundColor: "#de6b14",
            color: "#fff",
            minWidth: 220,
            borderRadius: 1,
            boxShadow: "0px 3px 12px rgba(0,0,0,0.3)",
            zIndex: 3000 + level,
            py: 1,
          }}
        >
          <List dense>
            {item.submenu.map((sub) => (
              <Box key={sub.name}>
                <ListItem
                  button
                  component={sub.submenu ? "div" : NavLink}
                  to={sub.link || sub.path}
                  onClick={() => {
                    setActiveSubmenu(sub.name);
                    setHoveredItem(null);
                    setHoverPositions({});
                  }}
                  onMouseEnter={(e) => handleMouseEnter(e, sub, level + 1)}
                  onMouseLeave={() => handleMouseLeave(sub, level + 1)}
                  sx={{
                    pl: 2,
                    color: isItemActive(sub) || activeSubmenu === sub.name ? "#de6b14" : "#fff",
                    backgroundColor: isItemActive(sub) || activeSubmenu === sub.name ? "#fff" : "#de6b14",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "#fff",
                      color: "#de6b14",
                    },
                  }}
                >
                  <ListItemText primary={sub.name} />
                  {sub.submenu && (
                    <ExpandMore
                      sx={{ color: isItemActive(sub) || activeSubmenu === sub.name ? "#de6b14" : "#fff" }}
                    />
                  )}
                </ListItem>

                {/* Sub-sub menu for collapsed mode */}
                {!open && sub.submenu && hoverPath.includes(sub.name) && hoverPositions[level + 1] && (
                  <Paper
                    onMouseEnter={() => {
                      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
                      setHoverPath((prev) => [...prev.slice(0, level + 2), sub.name]);
                    }}
                    onMouseLeave={() => handleMouseLeave(sub, level + 1)}
                    sx={{
                      position: "fixed",
                      top: hoverPositions[level + 1]?.top || 0,
                      left: (hoverPositions[level]?.left || 0) + 220,
                      backgroundColor: "#de6b14",
                      color: "#fff",
                      minWidth: 220,
                      borderRadius: 1,
                      boxShadow: "0px 3px 12px rgba(0,0,0,0.3)",
                      zIndex: 3000 + level + 1,
                      py: 1,
                    }}
                  >
                    <List dense>
                      {sub.submenu.map((subSub) => (
                        <ListItem
                          key={subSub.name}
                          button
                          component={subSub.submenu ? "div" : NavLink}
                          to={subSub.link || subSub.path}
                          onClick={() => {
                            setActiveSubmenu(subSub.name);
                            setHoveredItem(null);
                            setHoverPositions({});
                          }}
                          onMouseEnter={(e) => handleMouseEnter(e, subSub, level + 2)}
                          onMouseLeave={() => handleMouseLeave(subSub, level + 2)}
                          sx={{
                            pl: 2,
                            color: isItemActive(subSub) || activeSubmenu === subSub.name ? "#de6b14" : "#fff",
                            backgroundColor: isItemActive(subSub) || activeSubmenu === subSub.name ? "#fff" : "#de6b14",
                            fontWeight: 500,
                            "&:hover": {
                              backgroundColor: "#fff",
                              color: "#de6b14",
                            },
                          }}
                        >
                          <ListItemText primary={subSub.name} />
                          {subSub.submenu && (
                            <ExpandMore
                              sx={{ color: isItemActive(subSub) || activeSubmenu === subSub.name ? "#de6b14" : "#fff" }}
                            />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            ))}
          </List>
        </Paper>
      )}

      {/* Expanded sidebar collapse */}
      {open && item.submenu && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.submenu.map((sub) => renderItem(sub, level + 1))}
          </List>
        </Collapse>
      )}
    </Box>
  );
};

  return (
    <div className="flex">
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "#333",
        }}
      >
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={toggleDrawer} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <CustomTooltip title={userEmail} placement="right" arrow>
            <IconButton color="inherit">
              <span className="text-sm">{userEmail}</span>
            </IconButton>
          </CustomTooltip>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: open ? 240 : 60,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? 240 : 60,
            transition: "width 0.3s ease",
            mt: "64px",
            backgroundColor: "#de6b14",
            color: "#fff",
            height: "calc(100vh - 64px)",
            overflowY: "auto",
          },
        }}
      >
        <List>
          <ListItem
            button
            onClick={toggleDrawer}
            sx={{
              backgroundColor: open ? "rgba(0,0,0,0.1)" : "transparent",
              color: "#fff",
            }}
          >
            <CustomTooltip title="Navigation Menu" placement="right" arrow>
              <MenuSharp />
            </CustomTooltip>
          </ListItem>

          {sidebar.buyer.map((item, index) => (
            <Box key={`${item.name}-${index}`}>
              {renderItem(item, 0)}
              <Divider sx={{ borderColor: "rgba(255,255,255,0.3)", my: 0.5 }} />
            </Box>
          ))}
        </List>
      </Drawer>
    </div>
  );
};

export default Sidebar;